import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Eye, X, Plus, Pencil, Trash2, FileDown, Check, XCircle, Ban, History } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { pdf } from '@react-pdf/renderer';
import AdminLayout from '../../components/admin/AdminLayout';
import ReservationsListPDF from '../../components/pdf/ReservationsListPDF';
import type { ReservationRow, BookingParticipant } from '../../components/pdf/ReservationsListPDF';
import { GET_BOOKINGS } from '../../graphql/queries/booking';
import { GET_PACKAGE } from '../../graphql/admin/queries/package';
import { DELETE_BOOKING, ACCEPT_BOOKING, REJECT_BOOKING, CANCEL_BOOKING } from '../../graphql/mutations/booking';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

function BookingStatusBadge({ status, statutDisponibilite }: { status: string; statutDisponibilite: string }) {
  let color: 'green' | 'blue' | 'yellow' | 'red' | 'gray' | 'slate' = 'gray';
  let label = status;

  if (statutDisponibilite === 'annulee') {
    color = 'slate';
    label = 'Annulé';
  } else if (statutDisponibilite === 'disponible') {
    if (status === 'fully_paid' || status === 'paid_partner' || status === 'paid_organizer') {
      color = 'green';
      label = 'Payé';
    } else if (status === 'deposit_paid') {
      color = 'blue';
      label = 'Acompte payé';
    } else {
      color = 'yellow';
      label = 'Accepté - Non payé';
    }
  } else if (statutDisponibilite === 'non_disponible') {
    color = 'red';
    label = 'Refusé';
  } else {
    color = 'gray';
    label = 'En attente';
  }

  const colorClasses = {
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
    slate: 'bg-slate-200 text-slate-900',
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses[color as keyof typeof colorClasses]}`}>
      {label}
    </span>
  );
}

export default function PackageClientsPage() {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const packageIdNum = parseInt(packageId || '0', 10);
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<any>(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  // Récupérer les informations du package
  const { data: packageData, loading: packageLoading } = useQuery(GET_PACKAGE, {
    variables: { id: packageIdNum },
    skip: !packageIdNum,
  });

  // Récupérer l'ID de l'agence depuis localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userAgencyId = user.agencyId || user.agency?.id;

  // Récupérer toutes les réservations de l'agence
  const { data: bookingsData, loading: bookingsLoading, error: bookingsError, refetch: refetchBookings } = useQuery(GET_BOOKINGS, {
    variables: { agencyId: userAgencyId },
    skip: !userAgencyId,
    fetchPolicy: 'network-only',
  });

  // Toutes les réservations liées à ce package
  const packageBookings = React.useMemo(() => {
    if (!bookingsData?.bookings || !packageIdNum) return [];
    return bookingsData.bookings.filter((booking: any) => booking.packageId === packageIdNum);
  }, [bookingsData, packageIdNum]);

  /** Comptées comme « réservations du package » : uniquement après acceptation (disponible). */
  const packageBookingsConfirmed = React.useMemo(
    () =>
      packageBookings.filter(
        (b: any) => b.statutDisponibilite === 'disponible' && b.status !== 'CANCELLED'
      ),
    [packageBookings]
  );

  const packageBookingsPending = React.useMemo(
    () =>
      packageBookings.filter(
        (b: any) =>
          (b.statutDisponibilite || 'attente') === 'attente' && b.status !== 'CANCELLED'
      ),
    [packageBookings]
  );

  const packageBookingsRefused = React.useMemo(
    () => packageBookings.filter((b: any) => b.status === 'REJECTED'),
    [packageBookings]
  );

  const packageBookingsCancelled = React.useMemo(
    () => packageBookings.filter((b: any) => b.status === 'CANCELLED'),
    [packageBookings]
  );

  // Personnes comptabilisées sur le groupe : seulement réservations confirmées
  const totalPersonnesReservees = React.useMemo(() => {
    return packageBookingsConfirmed.reduce((sum: number, b: any) => {
      return sum + (Number(b.nbrAdulte) || 0) + (Number(b.nbrEnfant) || 0) + (Number(b.nbrBebe) || 0);
    }, 0);
  }, [packageBookingsConfirmed]);

  // Extraire le nombre max du groupe depuis groupSize (ex. "50", "20-50", "20 à 50" -> 50)
  const groupSizeMax = React.useMemo(() => {
    const gs = packageData?.package?.groupSize;
    if (!gs || typeof gs !== 'string') return null;
    const numbers = gs.match(/\d+/g);
    if (!numbers || numbers.length === 0) return null;
    return Math.max(...numbers.map((n) => parseInt(n, 10)));
  }, [packageData?.package?.groupSize]);

  const [deleteBooking, { loading: deleteLoading }] = useMutation(DELETE_BOOKING, {
    refetchQueries: [{ query: GET_BOOKINGS, variables: { agencyId: userAgencyId } }],
    onCompleted: () => setBookingToDelete(null),
    onError: (err) => {
      console.error(err);
      setBookingToDelete(null);
    },
  });

  const [bookingActionId, setBookingActionId] = useState<number | null>(null);
  const [bookingTab, setBookingTab] = useState<'confirmed' | 'pending' | 'cancelled' | 'refused'>('confirmed');

  const [acceptBooking] = useMutation(ACCEPT_BOOKING, {
    refetchQueries: [{ query: GET_BOOKINGS, variables: { agencyId: userAgencyId } }],
  });
  const [rejectBooking] = useMutation(REJECT_BOOKING, {
    refetchQueries: [{ query: GET_BOOKINGS, variables: { agencyId: userAgencyId } }],
  });

  const handleAcceptBooking = async (id: number) => {
    setBookingActionId(id);
    try {
      await acceptBooking({ variables: { id } });
    } catch (e) {
      console.error(e);
    } finally {
      setBookingActionId(null);
    }
  };

  const handleRejectBooking = async (id: number) => {
    setBookingActionId(id);
    try {
      await rejectBooking({ variables: { id } });
    } catch (e) {
      console.error(e);
    } finally {
      setBookingActionId(null);
    }
  };

  const [cancelBookingAgence] = useMutation(CANCEL_BOOKING, {
    refetchQueries: [{ query: GET_BOOKINGS, variables: { agencyId: userAgencyId } }],
  });

  const handleCancelByAgency = async (id: number) => {
    if (
      !window.confirm(
        'Annuler ce voyage ? La réservation sera marquée comme annulée par l’agence et les places seront libérées.'
      )
    ) {
      return;
    }
    setBookingActionId(id);
    try {
      await cancelBookingAgence({ variables: { id, annulationPar: 'agence' } });
    } catch (e) {
      console.error(e);
      window.alert(
        e instanceof Error ? e.message : "Impossible d'annuler cette réservation pour le moment."
      );
    } finally {
      setBookingActionId(null);
    }
  };

  const annulationParLabel = (p: string | null | undefined) =>
    p === 'client' ? 'Client' : p === 'agence' ? 'Agence' : p ? String(p) : '—';

  const handleDeleteConfirm = () => {
    if (!bookingToDelete?.id) return;
    deleteBooking({ variables: { id: bookingToDelete.id } });
  };

  const getStatusLabel = (booking: any) => {
    const statutDisponibilite = booking.statutDisponibilite || 'attente';
    if (statutDisponibilite === 'disponible') {
      if (booking.paiement === 'paye' || (booking.montantPaye && booking.montantPaye === booking.totalPrice)) return 'Payé';
      if (booking.paiement === 'avance' || (booking.montantAvance > 0)) return 'Acompte payé';
      return 'Accepté - Non payé';
    }
    if (statutDisponibilite === 'non_disponible') return 'Refusé';
    return 'En attente';
  };

  const formatBookingDate = (value: string | number | Date | null | undefined): string => {
    if (value == null || value === '') return '—';
    try {
      let date: Date;
      if (value instanceof Date) {
        date = value;
      } else if (typeof value === 'number') {
        date = value < 1e12 ? new Date(value * 1000) : new Date(value);
      } else {
        const str = String(value).trim();
        if (!str) return '—';
        const num = /^\d+$/.test(str) ? parseInt(str, 10) : NaN;
        if (!Number.isNaN(num)) {
          date = num < 1e12 ? new Date(num * 1000) : new Date(num);
        } else {
          date = new Date(str.replace(' ', 'T'));
        }
      }
      if (isNaN(date.getTime())) return '—';
      return format(date, 'dd/MM/yyyy', { locale: fr });
    } catch {
      return '—';
    }
  };

  const clientDisplayName = (booking: any): string =>
    booking.nom && booking.prenom
      ? `${booking.nom} ${booking.prenom}`
      : booking.client
        ? `${booking.client.firstName || ''} ${booking.client.lastName || ''}`.trim()
        : 'Client inconnu';

  const exportToPdf = async () => {
    const pkg = packageData?.package;
    if (!pkg || packageBookingsConfirmed.length === 0) return;
    setPdfExporting(true);
    try {
      const clientNameFor = (booking: any) =>
        booking.nom && booking.prenom
          ? `${booking.nom} ${booking.prenom}`
          : booking.client
            ? `${booking.client.firstName || ''} ${booking.client.lastName || ''}`.trim()
            : 'N/A';

      const rows: ReservationRow[] = packageBookingsConfirmed.map((booking: any) => {
        return {
          id: booking.id,
          client: clientNameFor(booking),
          email: booking.mail || booking.client?.email || '',
          phone: booking.telephone || booking.client?.phone || '',
          statut: getStatusLabel(booking),
          paiement: booking.paiement === 'paye' ? 'Payé' : booking.paiement === 'avance' ? 'Avance' : 'Non payé',
          avance: String(booking.montantAvance ?? ''),
          paye: String(booking.montantPaye ?? ''),
          adultes: booking.nbrAdulte ?? 0,
          enfants: booking.nbrEnfant ?? 0,
          bebes: booking.nbrBebe ?? 0,
          total: booking.totalPrice != null ? `${Number(booking.totalPrice).toLocaleString('fr-FR')} DA` : '',
          date: formatBookingDate(booking.createdAt),
          formule: booking.formula?.name || '',
          typeReservation:
            booking.typeReservation === 'agence'
              ? 'Agence'
              : booking.typeReservation === 'partenaire'
                ? 'Partenaire'
                : booking.typeReservation === 'enligne'
                  ? 'En ligne'
                  : booking.typeReservation || '',
        };
      });

      const participantsByBooking: BookingParticipant[] = packageBookingsConfirmed.map((booking: any) => {
        const personnes = Array.isArray(booking.personnes) ? booking.personnes : [];
        const bebes = Array.isArray(booking.bebes) ? booking.bebes : [];
        return {
          bookingId: booking.id,
          clientName: clientNameFor(booking),
          personnes: personnes.map((p: any) => ({
            type: p.type || 'adulte',
            nom: p.nom || '',
            prenom: p.prenom || '',
            chambre: p.chambre,
          })),
          bebes: bebes.map((b: any) => ({
            nom: b.nom || '',
            prenom: b.prenom || '',
            chambre: b.chambre,
          })),
        };
      });

      const generatedAt = format(new Date(), 'dd/MM/yyyy HH:mm', { locale: fr });
      const blob = await pdf(
        <ReservationsListPDF
          packageTitle={pkg.title || 'Package'}
          packageId={String(packageId)}
          rows={rows}
          generatedAt={generatedAt}
          participantsByBooking={participantsByBooking}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reservations-${(pkg.title || 'package').replace(/[^a-z0-9-_]/gi, '-')}-${packageId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setPdfExporting(false);
    }
  };

  if (packageLoading || bookingsLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg">Chargement des données...</div>
        </div>
      </AdminLayout>
    );
  }

  if (bookingsError) {
    return (
      <AdminLayout>
        <div className="text-red-600">
          Erreur lors du chargement des réservations: {bookingsError.message}
        </div>
      </AdminLayout>
    );
  }

  const pkg = packageData?.package;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <button
            onClick={() => navigate('/admin/packages')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour aux packages
          </button>
        </div>

        {/* En-tête avec informations du package */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Réservations du package
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {pkg?.title || 'Chargement...'}
                </p>
                <p className="text-xs text-gray-500 mt-2 max-w-xl">
                  Toutes les réservations sont regroupées dans les onglets ci-dessous. L’onglet « Confirmées » (vue
                  principale) affiche les places comptées pour le groupe.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {packageBookingsConfirmed.length || 0} confirmée
                  {packageBookingsConfirmed.length !== 1 ? 's' : ''}
                </span>
                {packageBookingsPending.length > 0 && (
                  <span className="px-3 py-1 bg-amber-100 text-amber-900 rounded-full text-sm font-medium">
                    {packageBookingsPending.length} demande{packageBookingsPending.length !== 1 ? 's' : ''}{' '}
                    en attente
                  </span>
                )}
                <span className="px-3 py-1 bg-slate-100 text-slate-800 rounded-full text-sm font-medium">
                  {totalPersonnesReservees} personne{totalPersonnesReservees !== 1 ? 's' : ''}
                  {groupSizeMax != null ? ` / ${groupSizeMax}` : ''}
                </span>
                <button
                  type="button"
                  onClick={exportToPdf}
                  disabled={packageBookingsConfirmed.length === 0 || pdfExporting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Exporter en PDF"
                >
                  {pdfExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Génération...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-4 h-4" />
                      Exporter en PDF
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/admin/bookings/new/${packageId}`)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                  Réserver ce package
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des réservations — onglets (Confirmées = vue principale par défaut) */}
        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
          <div className="px-4 pt-4 pb-0 border-b border-gray-100 bg-gray-50/70">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900">Liste des réservations</h4>
              <button
                onClick={() => refetchBookings()}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded hover:bg-gray-100"
                title="Rafraîchir"
              >
                <History className="w-3.5 h-3.5" /> Rafraîchir
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">
              Onglet « Confirmées » ouvert par défaut (places du groupe). Les autres onglets regroupent les demandes
              en attente, les annulations et les refus.
            </p>
            <div
              className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1"
              role="tablist"
              aria-label="Filtrer par statut"
            >
              <button
                type="button"
                role="tab"
                aria-selected={bookingTab === 'confirmed'}
                onClick={() => setBookingTab('confirmed')}
                className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  bookingTab === 'confirmed'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Confirmées ({packageBookingsConfirmed.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={bookingTab === 'pending'}
                onClick={() => setBookingTab('pending')}
                className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  bookingTab === 'pending'
                    ? 'bg-amber-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                En attente ({packageBookingsPending.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={bookingTab === 'cancelled'}
                onClick={() => setBookingTab('cancelled')}
                className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  bookingTab === 'cancelled'
                    ? 'bg-slate-700 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Annulées ({packageBookingsCancelled.length})
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={bookingTab === 'refused'}
                onClick={() => setBookingTab('refused')}
                className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  bookingTab === 'refused'
                    ? 'bg-red-700 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Refusées ({packageBookingsRefused.length})
              </button>
            </div>
          </div>

          <div className="bg-white" role="tabpanel">
            {bookingTab === 'confirmed' && (
              <>
                <div className="px-4 py-3 border-b border-indigo-100 bg-indigo-50/40">
                  <p className="text-xs text-indigo-950">
                    Vue principale — places comptées pour le groupe (demandes acceptées ou saisies agence comme
                    disponibles).
                  </p>
                </div>
                {packageBookings.length === 0 ? (
                  <div className="px-4 py-12 text-center text-gray-500">
                    <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium">Aucune réservation pour ce package</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Les réservations apparaîtront ici une fois créées
                    </p>
                  </div>
                ) : packageBookingsConfirmed.length === 0 ? (
                  <div className="px-4 py-12 text-center text-gray-500">
                    <p className="text-lg font-medium">Aucune réservation confirmée pour l&apos;instant</p>
                    <p className="text-sm text-gray-400 mt-2">
                      {packageBookingsPending.length > 0
                        ? 'Passez à l’onglet « En attente » pour accepter des demandes et les ajouter au groupe.'
                        : 'Créez une réservation depuis l&apos;admin ou attendez des demandes clients.'}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Client
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="hidden px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Statut
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Paiement
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Voyageurs
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Montant
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {packageBookingsConfirmed.map((booking: any) => {
                          let status = 'pending_availability';
                          if (booking.statutDisponibilite === 'disponible') {
                            if (
                              booking.paiement === 'paye' ||
                              (booking.montantPaye && booking.montantPaye === booking.totalPrice)
                            ) {
                              status = 'fully_paid';
                            } else if (
                              booking.paiement === 'avance' ||
                              (booking.montantAvance && booking.montantAvance > 0)
                            ) {
                              status = 'deposit_paid';
                            } else {
                              status = 'accepted_unpaid';
                            }
                          } else if (booking.statutDisponibilite === 'non_disponible') {
                            status = 'rejected';
                          }

                          const clientName = clientDisplayName(booking);
                          const clientEmail = booking.mail || booking.client?.email || '';
                          const clientPhone = booking.telephone || booking.client?.phone || '';

                          let dateDisplay = 'N/A';
                          try {
                            if (booking.createdAt) {
                              let date: Date;
                              if (typeof booking.createdAt === 'string') {
                                date = new Date(booking.createdAt);
                                if (isNaN(date.getTime())) {
                                  const timestamp = parseInt(booking.createdAt, 10);
                                  if (!isNaN(timestamp)) {
                                    date = new Date(timestamp < 1e12 ? timestamp * 1000 : timestamp);
                                  } else {
                                    date = new Date(booking.createdAt.replace(' ', 'T'));
                                  }
                                }
                              } else if (booking.createdAt instanceof Date) {
                                date = booking.createdAt;
                              } else {
                                date = new Date(NaN);
                              }
                              if (!isNaN(date.getTime())) {
                                dateDisplay = format(date, 'dd MMM yyyy', { locale: fr });
                              } else if (typeof booking.createdAt === 'string') {
                                dateDisplay = booking.createdAt.split('T')[0] || 'N/A';
                              }
                            }
                          } catch {
                            if (typeof booking.createdAt === 'string') {
                              const parts = booking.createdAt.split('T')[0].split('-');
                              dateDisplay =
                                parts.length === 3
                                  ? `${parts[2]}/${parts[1]}/${parts[0]}`
                                  : booking.createdAt || 'N/A';
                            } else {
                              dateDisplay = 'N/A';
                            }
                          }

                          return (
                            <tr key={booking.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <User className="w-5 h-5 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{clientName}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{clientEmail}</div>
                                <div className="text-sm text-gray-500">{clientPhone}</div>
                              </td>
                              <td className="hidden px-6 py-4 whitespace-nowrap">
                                <BookingStatusBadge
                                  status={status}
                                  statutDisponibilite={booking.statutDisponibilite || 'attente'}
                                />
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.paiement === 'paye'
                                    ? 'Payé'
                                    : booking.paiement === 'avance'
                                      ? 'Avance'
                                      : 'Non payé'}
                                </div>
                                {booking.montantAvance > 0 && (
                                  <div className="text-xs text-gray-500">
                                    Avance: {booking.montantAvance.toLocaleString('fr-FR')} DA
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {booking.nbrAdulte || 0} adulte{(booking.nbrAdulte || 0) > 1 ? 's' : ''}
                                </div>
                                {(booking.nbrEnfant || 0) > 0 && (
                                  <div className="text-xs text-gray-500">
                                    {booking.nbrEnfant} enfant{(booking.nbrEnfant || 0) > 1 ? 's' : ''}
                                  </div>
                                )}
                                {(booking.nbrBebe || 0) > 0 && (
                                  <div className="text-xs text-gray-500">
                                    {booking.nbrBebe} bébé{(booking.nbrBebe || 0) > 1 ? 's' : ''}
                                  </div>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="text-sm font-medium text-gray-900">
                                  {booking.totalPrice?.toLocaleString('fr-FR') || 0} DA
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                                  <span className="text-sm text-gray-900">{dateDisplay}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedBooking(booking);
                                      setIsDetailsModalOpen(true);
                                    }}
                                    className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="Voir les détails"
                                  >
                                    <Eye className="w-5 h-5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/admin/bookings/${booking.id}/history`)}
                                    className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition-colors"
                                    title="Historique des modifications"
                                  >
                                    <History className="w-5 h-5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/admin/bookings/edit/${booking.id}`)}
                                    className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded transition-colors"
                                    title="Modifier"
                                  >
                                    <Pencil className="w-5 h-5" />
                                  </button>
                                  <button
                                    type="button"
                                    disabled={bookingActionId === booking.id}
                                    onClick={() => handleCancelByAgency(booking.id)}
                                    className="p-2 text-orange-700 hover:text-orange-900 hover:bg-orange-50 rounded transition-colors disabled:opacity-50"
                                    title="Annuler le voyage (agence)"
                                  >
                                    {bookingActionId === booking.id ? (
                                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-orange-700 border-t-transparent" />
                                    ) : (
                                      <Ban className="w-5 h-5" />
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setBookingToDelete(booking)}
                                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {bookingTab === 'pending' && (
              <>
                {packageBookingsPending.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucune demande en attente pour ce package.
                  </div>
                ) : (
                  <div className="border-t border-amber-100 bg-amber-50/40">
                    <div className="px-4 py-3 border-b border-amber-100 bg-amber-50/80">
                      <p className="text-xs text-amber-950">
                        Acceptez une demande pour la confirmer et l&apos;ajouter au groupe, ou refusez si vous ne
                        pouvez pas l&apos;honorer.
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-amber-100">
                        <thead className="bg-amber-100/80">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Client
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Contact
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Origine
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Voyageurs
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Montant
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-amber-950 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-amber-100">
                          {packageBookingsPending.map((booking: any) => {
                            const clientEmail = booking.mail || booking.client?.email || '';
                            const clientPhone = booking.telephone || booking.client?.phone || '';
                            const typeLabel =
                              booking.typeReservation === 'agence'
                                ? 'Agence'
                                : booking.typeReservation === 'partenaire'
                                  ? 'Partenaire'
                                  : booking.typeReservation === 'enligne'
                                    ? 'En ligne'
                                    : booking.typeReservation || '—';
                            const actionBusy = bookingActionId === booking.id;
                            return (
                              <tr key={booking.id} className="hover:bg-amber-50/40">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <User className="w-5 h-5 text-amber-700/70 mr-2" />
                                    <span className="text-sm font-medium text-gray-900">
                                      {clientDisplayName(booking)}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{clientEmail}</div>
                                  <div className="text-sm text-gray-500">{clientPhone}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{typeLabel}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {(Number(booking.nbrAdulte) || 0) +
                                    (Number(booking.nbrEnfant) || 0) +
                                    (Number(booking.nbrBebe) || 0)}{' '}
                                  voyageur
                                  {(Number(booking.nbrAdulte) || 0) +
                                    (Number(booking.nbrEnfant) || 0) +
                                    (Number(booking.nbrBebe) || 0) !==
                                  1
                                    ? 's'
                                    : ''}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {booking.totalPrice != null
                                    ? `${Number(booking.totalPrice).toLocaleString('fr-FR')} DA`
                                    : '—'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <Calendar className="w-4 h-4 text-amber-700/60 mr-1" />
                                    <span className="text-sm text-gray-900">
                                      {formatBookingDate(booking.createdAt)}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex flex-wrap items-center gap-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedBooking(booking);
                                        setIsDetailsModalOpen(true);
                                      }}
                                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                      title="Voir les détails"
                                    >
                                      <Eye className="w-5 h-5" />
                                    </button>
                                    <button
                                      type="button"
                                      disabled={actionBusy}
                                      onClick={() => handleAcceptBooking(booking.id)}
                                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-green-800 bg-green-100 hover:bg-green-200 disabled:opacity-50 text-xs font-medium"
                                      title="Accepter la demande"
                                    >
                                      {actionBusy ? (
                                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-green-800 border-t-transparent" />
                                      ) : (
                                        <Check className="w-4 h-4" />
                                      )}
                                      Accepter
                                    </button>
                                    <button
                                      type="button"
                                      disabled={actionBusy}
                                      onClick={() => handleRejectBooking(booking.id)}
                                      className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-red-800 bg-red-100 hover:bg-red-200 disabled:opacity-50 text-xs font-medium"
                                      title="Refuser la demande"
                                    >
                                      <XCircle className="w-4 h-4" />
                                      Refuser
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/admin/bookings/${booking.id}/history`)}
                                      className="p-2 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded transition-colors"
                                      title="Historique des modifications"
                                    >
                                      <History className="w-5 h-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/admin/bookings/edit/${booking.id}`)}
                                      className="p-2 text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded transition-colors"
                                      title="Modifier"
                                    >
                                      <Pencil className="w-5 h-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setBookingToDelete(booking)}
                                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {bookingTab === 'cancelled' && (
              <>
                {packageBookingsCancelled.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucun voyage annulé pour ce package.
                  </div>
                ) : (
                  <div className="border-t border-slate-100">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/90">
                      <p className="text-xs text-slate-700">
                        Places libérées. La colonne « Annulé par » indique si l’annulation vient du client ou de
                        l’agence.
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                              Client
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                              Contact
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                              Annulé par
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                              Date d’annulation
                            </th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-slate-700 uppercase">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {packageBookingsCancelled.map((booking: any) => (
                            <tr key={booking.id} className="hover:bg-slate-50/80">
                              <td className="px-6 py-3 text-sm text-gray-900">{clientDisplayName(booking)}</td>
                              <td className="px-6 py-3 text-sm text-gray-600">
                                <div>{booking.mail || booking.client?.email || '—'}</div>
                                <div className="text-gray-400">{booking.telephone || booking.client?.phone || ''}</div>
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-800">
                                {annulationParLabel(booking.annulationPar)}
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-700">
                                {booking.annuleLe ? formatBookingDate(booking.annuleLe) : '—'}
                              </td>
                              <td className="px-6 py-3 text-sm">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedBooking(booking);
                                      setIsDetailsModalOpen(true);
                                    }}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Voir les détails"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setBookingToDelete(booking)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {bookingTab === 'refused' && (
              <>
                {packageBookingsRefused.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-gray-500">
                    Aucune demande refusée pour ce package.
                  </div>
                ) : (
                  <div className="border-t border-gray-100">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/90">
                      <p className="text-xs text-gray-600">Demandes refusées — non comptées dans le groupe.</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-600 uppercase">Client</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-600 uppercase">Contact</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-600 uppercase">Date</th>
                            <th className="px-6 py-2 text-left text-xs font-medium text-gray-600 uppercase">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {packageBookingsRefused.map((booking: any) => (
                            <tr key={booking.id} className="hover:bg-gray-50">
                              <td className="px-6 py-3 text-sm text-gray-900">{clientDisplayName(booking)}</td>
                              <td className="px-6 py-3 text-sm text-gray-600">
                                <div>{booking.mail || booking.client?.email || '—'}</div>
                                <div className="text-gray-400">{booking.telephone || booking.client?.phone || ''}</div>
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-700">
                                {formatBookingDate(booking.createdAt)}
                              </td>
                              <td className="px-6 py-3 text-sm">
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedBooking(booking);
                                      setIsDetailsModalOpen(true);
                                    }}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                    title="Voir les détails"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setBookingToDelete(booking)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Modal de détails */}
        {isDetailsModalOpen && selectedBooking && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => setIsDetailsModalOpen(false)}
              />
              <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 max-h-[80vh] overflow-y-auto">
                  <div className="flex items-start justify-between mb-6 border-b border-gray-100 pb-3">
                    <h3 className="text-xl font-semibold leading-7 text-gray-900">
                      Détails de la réservation #{selectedBooking.id}
                    </h3>
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
                      onClick={() => setIsDetailsModalOpen(false)}
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="font-semibold text-gray-800 mb-3">Informations générales</h4>
                      <div className="space-y-2 text-sm text-gray-800">
                        <div><span className="font-medium">Référence:</span> #{selectedBooking.id}</div>
                        <div><span className="font-medium">Formule:</span> {selectedBooking.formula?.name || 'Non spécifiée'}</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="font-semibold text-gray-800 mb-3">Contact principal</h4>
                      <div className="space-y-2 text-sm text-gray-800">
                        <div><span className="font-medium">Nom:</span> {selectedBooking.nom && selectedBooking.prenom 
                          ? `${selectedBooking.nom} ${selectedBooking.prenom}`
                          : selectedBooking.client 
                            ? `${selectedBooking.client.firstName} ${selectedBooking.client.lastName}`
                            : 'N/A'}</div>
                        <div><span className="font-medium">Email:</span> {selectedBooking.mail || selectedBooking.client?.email || 'N/A'}</div>
                        <div><span className="font-medium">Téléphone:</span> {selectedBooking.telephone || selectedBooking.client?.phone || 'N/A'}</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="font-semibold text-gray-800 mb-3">Voyageurs</h4>
                      <div className="space-y-2 text-sm text-gray-800">
                        <div><span className="font-medium">Adultes:</span> {selectedBooking.nbrAdulte || 0}</div>
                        <div><span className="font-medium">Enfants:</span> {selectedBooking.nbrEnfant || 0}</div>
                        <div><span className="font-medium">Bébés:</span> {selectedBooking.nbrBebe || 0}</div>
                        <div><span className="font-medium">Total:</span> {(selectedBooking.nbrAdulte || 0) + (selectedBooking.nbrEnfant || 0) + (selectedBooking.nbrBebe || 0)} voyageur{((selectedBooking.nbrAdulte || 0) + (selectedBooking.nbrEnfant || 0) + (selectedBooking.nbrBebe || 0)) > 1 ? 's' : ''}</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="font-semibold text-gray-800 mb-3">Chambres</h4>
                      <div className="space-y-2 text-sm text-gray-800">
                        <div><span className="font-medium">Nombre:</span> {selectedBooking.nbrChambre || 0}</div>
                        {selectedBooking.chambres && Array.isArray(selectedBooking.chambres) && selectedBooking.chambres.length > 0 && (
                          <div>
                            <span className="font-medium">Types:</span>
                            <ul className="list-disc list-inside mt-1 ml-4">
                              {selectedBooking.chambres.map((chambre: any, index: number) => (
                                <li key={index}>
                                  Chambre {index + 1}: {chambre.type || 'Non spécifié'}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {selectedBooking.personnes && Array.isArray(selectedBooking.personnes) && selectedBooking.personnes.length > 0 && (
                      <div className="col-span-2 bg-white rounded-lg p-4 border border-gray-100">
                        <h4 className="font-semibold text-gray-800 mb-3">Liste des voyageurs</h4>
                        <div className="space-y-2 text-sm text-gray-800">
                          {selectedBooking.personnes.map((personne: any, index: number) => (
                            <div key={index} className="border-l-2 border-gray-200 pl-3">
                              <span className="font-medium">{personne.type === 'adulte' ? 'Adulte' : 'Enfant'} {index + 1}:</span> {personne.nom || ''} {personne.prenom || ''}
                              {personne.chambre && <span className="text-gray-500 ml-2">(Chambre {personne.chambre})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {selectedBooking.bebes && Array.isArray(selectedBooking.bebes) && selectedBooking.bebes.length > 0 && (
                      <div className="col-span-2 bg-white rounded-lg p-4 border border-gray-100">
                        <h4 className="font-semibold text-gray-800 mb-3">Liste des bébés</h4>
                        <div className="space-y-2 text-sm text-gray-800">
                          {selectedBooking.bebes.map((bebe: any, index: number) => (
                            <div key={index} className="border-l-2 border-gray-200 pl-3">
                              <span className="font-medium">Bébé {index + 1}:</span> {bebe.nom || ''} {bebe.prenom || ''}
                              {bebe.chambre && <span className="text-gray-500 ml-2">(Chambre {bebe.chambre})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="font-semibold text-gray-800 mb-3">Paiement</h4>
                      <div className="space-y-2 text-sm text-gray-800">
                        <div><span className="font-medium">Statut:</span> {
                          selectedBooking.paiement === 'paye' ? 'Payé' :
                          selectedBooking.paiement === 'avance' ? 'Avance' :
                          'Non payé'
                        }</div>
                        {selectedBooking.montantAvance > 0 && (
                          <div><span className="font-medium">Montant avance:</span> {selectedBooking.montantAvance.toLocaleString('fr-FR')} DA</div>
                        )}
                        {selectedBooking.montantPaye > 0 && (
                          <div><span className="font-medium">Montant payé:</span> {selectedBooking.montantPaye.toLocaleString('fr-FR')} DA</div>
                        )}
                        <div><span className="font-medium">Montant total:</span> {selectedBooking.totalPrice?.toLocaleString('fr-FR') || 0} DA</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <h4 className="font-semibold text-gray-800 mb-3">Autres informations</h4>
                      <div className="space-y-2 text-sm text-gray-800">
                        {selectedBooking.specialRequests && (
                          <div>
                            <span className="font-medium">Demandes spéciales:</span>
                            <p className="text-gray-600 mt-1">{selectedBooking.specialRequests}</p>
                          </div>
                        )}
                        {selectedBooking.delaiOption && (
                          <div><span className="font-medium">Délai option:</span> {selectedBooking.delaiOption}</div>
                        )}
                        {selectedBooking.createdAt && (
                          <div><span className="font-medium">Date de création:</span> {
                            (() => {
                              try {
                                if (!selectedBooking.createdAt) return 'N/A';
                                let date: Date;
                                if (typeof selectedBooking.createdAt === 'string') {
                                  date = new Date(selectedBooking.createdAt);
                                  if (isNaN(date.getTime())) {
                                    const timestamp = parseInt(selectedBooking.createdAt);
                                    if (!isNaN(timestamp)) {
                                      date = new Date(timestamp);
                                    } else {
                                      date = new Date(selectedBooking.createdAt.replace(' ', 'T'));
                                    }
                                  }
                                } else if (selectedBooking.createdAt instanceof Date) {
                                  date = selectedBooking.createdAt;
                                } else {
                                  return 'N/A';
                                }
                                if (isNaN(date.getTime())) {
                                  return typeof selectedBooking.createdAt === 'string' 
                                    ? selectedBooking.createdAt.split('T')[0] 
                                    : 'N/A';
                                }
                                return format(date, 'dd MMM yyyy', { locale: fr });
                              } catch {
                                return 'N/A';
                              }
                            })()
                          }</div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsDetailsModalOpen(false);
                        navigate(`/admin/bookings/${selectedBooking.id}/history`);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      <History className="w-4 h-4" />
                      Historique
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsDetailsModalOpen(false);
                        navigate(`/admin/bookings/edit/${selectedBooking.id}`);
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700"
                    >
                      <Pencil className="w-4 h-4" />
                      Modifier
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                      onClick={() => setIsDetailsModalOpen(false)}
                    >
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmation de suppression */}
        {bookingToDelete && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                onClick={() => !deleteLoading && setBookingToDelete(null)}
              />
              <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
              <div className="relative inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:align-middle">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <h3 className="text-lg font-medium leading-6 text-gray-900 mb-2">
                    Supprimer la réservation ?
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    La réservation #{bookingToDelete.id}
                    {(bookingToDelete.nom || bookingToDelete.prenom || bookingToDelete.client)
                      ? ` (${[bookingToDelete.nom, bookingToDelete.prenom].filter(Boolean).join(' ') || (bookingToDelete.client ? `${bookingToDelete.client.firstName} ${bookingToDelete.client.lastName}` : '')})`
                      : ''}
                    sera définitivement supprimée.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      disabled={deleteLoading}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 disabled:opacity-50"
                      onClick={() => setBookingToDelete(null)}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      disabled={deleteLoading}
                      className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
                      onClick={handleDeleteConfirm}
                    >
                      {deleteLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          Suppression...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Supprimer
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
